import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONTROL_CLASS } from '@/components/tabel/stijl';

/** Zelfde stilte als de centrale serverfilters (§3). */
const ZOEK_VERTRAGING_MS = 300;

interface OrdersFilterBarProps {
  /** De zoekterm in de lijststand. */
  search: string;
  /** De drempel zoals getypt; de API toetst 1..90. */
  stalenessDays: string;
  stalenessPlaceholder: number;
  onSearch: (search: string) => void;
  onStalenessDays: (stalenessDays: string) => void;
}

/**
 * spec-tabelfilters story 11: de paginafilterrij van de orderlijst (CAP-13) — zoekveld en stilstanddrempel.
 * Klant, dienst, status en periode zijn kolomfilters in de centrale filterbalk.
 *
 * Het zoekveld toont de getypte tekst en geeft hem na 300 ms stilte door. Verandert de stand van buiten
 * (een link), dan volgt het veld en vervalt een lopende timer; "Filters wissen" hermonteert de rij.
 */
export function OrdersFilterBar({ search, stalenessDays, stalenessPlaceholder, onSearch, onStalenessDays }: OrdersFilterBarProps) {
  const { t } = useTranslation();
  const [tekst, setTekst] = useState(search);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timer.current);
    setTekst(search);
  }, [search]);
  useEffect(() => () => clearTimeout(timer.current), []);

  function typ(waarde: string) {
    setTekst(waarde);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearch(waarde), ZOEK_VERTRAGING_MS);
  }

  return (
    <div className="flex w-full flex-wrap items-end gap-3 md:w-auto" data-testid="orders-filter-bar">
      {/* fu-6/AC5: zoekt op ordernummer, klantnaam en klantnummer; niet op streepjescode, want die staat niet in de lijst. */}
      <input
        aria-label={t('orders.filters.search')}
        className={`${CONTROL_CLASS} w-full md:w-64`}
        data-testid="orders-search"
        placeholder={t('orders.filters.searchPlaceholder')}
        type="search"
        value={tekst}
        onChange={(event) => typ(event.target.value)}
      />
      <label className="flex items-center gap-2 text-xs text-[var(--foreground-muted)]">
        {t('orders.filters.stalenessDays')}
        <input
          aria-label={t('orders.filters.stalenessDays')}
          className={`${CONTROL_CLASS} w-20`}
          max={90}
          min={1}
          placeholder={String(stalenessPlaceholder)}
          type="number"
          value={stalenessDays}
          onChange={(event) => onStalenessDays(event.target.value)}
        />
      </label>
    </div>
  );
}
